import { EnumSurveyQuestionType, type Maybe } from "gql/generated";

type VisibilityQuestion = {
  _id?: Maybe<string>;
  type?: Maybe<EnumSurveyQuestionType | string>;
  parentQuestion?: Maybe<string>;
};

type VisibilityResponse = {
  questionData?: Maybe<VisibilityQuestion>;
  response?: Maybe<Maybe<string>[]>;
};

export const isToggleQuestionType = (
  type?: Maybe<EnumSurveyQuestionType | string>,
) => type === EnumSurveyQuestionType.Toggle;

export const isToggleOn = (response?: Maybe<Maybe<string>[]>) =>
  response?.[0] === "true";

/**
 * A question is visible unless it belongs to a toggle that is off. A question
 * whose toggle isn't in `responses` (removed or deactivated) is treated as a
 * regular, always-visible question. Mirrors the backend helper.
 */
export const isQuestionVisible = (
  question: Maybe<VisibilityQuestion> | undefined,
  responses: Maybe<VisibilityResponse>[],
) => {
  if (!question?.parentQuestion) return true;
  const parent = responses.find(
    r =>
      !!r?.questionData?._id && r.questionData._id === question.parentQuestion,
  );
  if (!parent || !isToggleQuestionType(parent.questionData?.type)) return true;
  return isToggleOn(parent.response);
};

/** Empties answers to questions hidden behind an off toggle. */
export const clearHiddenResponses = <T extends VisibilityResponse>(
  responses: T[],
): T[] =>
  responses.map(r =>
    isQuestionVisible(r.questionData, responses) ? r : { ...r, response: [] },
  );

/** Ids of questions hidden behind an off toggle, for answers keyed by question id. */
export const getHiddenQuestionIds = (
  questions: VisibilityQuestion[],
  responsesById: Record<string, Maybe<Maybe<string>[]> | undefined>,
) => {
  const responses = questions.map(q => ({
    questionData: q,
    response: q._id ? responsesById[q._id] : undefined,
  }));
  return new Set(
    responses
      .filter(r => !isQuestionVisible(r.questionData, responses))
      .map(r => r.questionData._id)
      .filter((id): id is string => !!id),
  );
};

export type SurveyQuestionGroup<T> = { question: T; children: T[] };

/**
 * Groups follow-up questions under their toggle, in survey order. Follow-ups
 * whose toggle is missing (or isn't a toggle) stay top-level.
 */
export const groupSurveyQuestions = <T extends VisibilityQuestion>(
  questions: T[],
): SurveyQuestionGroup<T>[] => {
  const toggleIds = new Set(
    questions
      .filter(q => isToggleQuestionType(q.type) && !!q._id)
      .map(q => q._id as string),
  );
  const isChild = (q: T) =>
    !!q.parentQuestion &&
    q.parentQuestion !== q._id &&
    toggleIds.has(q.parentQuestion) &&
    !isToggleQuestionType(q.type);

  const groups = questions
    .filter(q => !isChild(q))
    .map(question => ({ question, children: [] as T[] }));
  const groupById = new Map(
    groups
      .filter(g => !!g.question._id)
      .map(g => [g.question._id as string, g]),
  );
  questions.filter(isChild).forEach(child => {
    groupById.get(child.parentQuestion as string)?.children.push(child);
  });
  return groups;
};

export const flattenSurveyQuestionGroups = <T>(
  groups: SurveyQuestionGroup<T>[],
): T[] => groups.flatMap(g => [g.question, ...g.children]);
